import { ChevronDown } from 'lucide-react';
import React, { useState } from 'react';

interface PipelineStep {
  node: string;
  status?: string;
  valid?: boolean;
  complete?: boolean;
  node_count?: number;
  errors?: string[];
  missing?: string[];
  raw?: string;
  parsed?: string;
  nodes?: string;
  avap_code?: string;
  raw_len?: number;
  repair?: string;
}

interface PipelineStepsListProps {
  pipelineSteps: PipelineStep[];
  stepKeyPrefix: string;
  baseMarginLeft?: number;
}

const PIPELINE_LABEL: Record<string, string> = {
  pipeline_fetch_catalog: 'FETCH_CATALOG',
  pipeline_compile_avap: 'COMPILE_AVAP',
  pipeline_validate_bytecode: 'VALIDATE_BYTECODE',
  pipeline_syntax_validation: 'SYNTAX_VALIDATION',
  pipeline_completeness: 'COMPLETENESS_CHECK',
  pipeline_decompile: 'DECOMPILE',
};

export default function PipelineStepsList({ pipelineSteps, stepKeyPrefix, baseMarginLeft = 32 }: PipelineStepsListProps) {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  return (
    <>
      {pipelineSteps.map((ps, pi) => {
        const psRunning = ps.status === 'start';
        const psOk = ps.valid !== false && ps.complete !== false;
        const psKey = `${stepKeyPrefix}-ps-${pi}`;
        const isPsExpanded = expandedSteps.has(psKey);

        const togglePs = () => setExpandedSteps(prev => {
          const n = new Set(prev);
          n.has(psKey) ? n.delete(psKey) : n.add(psKey);
          return n;
        });

        const hasDetail = !!(
          (ps.errors && ps.errors.length > 0) ||
          (ps.missing && ps.missing.length > 0) ||
          ps.avap_code || ps.raw || ps.parsed || ps.nodes
        );

        const psLabel = PIPELINE_LABEL[ps.node] ?? ps.node.toUpperCase();

        const psMeta = ps.node === 'pipeline_syntax_validation' && ps.valid !== undefined
          ? (ps.valid ? `${ps.node_count ?? 0} nodes` : `${(ps.errors ?? []).length} error(s)`)
          : ps.node === 'pipeline_compile_avap' && ps.raw_len
            ? `${ps.raw_len} chars`
            : ps.node === 'pipeline_decompile' && ps.avap_code
              ? `${ps.avap_code.length} chars`
              : ps.node === 'pipeline_completeness' && ps.complete !== undefined
                ? (ps.complete ? 'OK' : 'incomplete')
                : ps.node === 'pipeline_validate_bytecode' && ps.valid !== undefined
                  ? (ps.repair && ps.repair !== 'none' ? `repaired` : 'ok')
                  : null;

        return (
          <React.Fragment key={pi}>
            <div className="bx-step-connector" style={{ marginLeft: baseMarginLeft }} />
            <div
              className="bx-step-row"
              style={{ paddingLeft: baseMarginLeft, cursor: hasDetail ? 'pointer' : 'default' }}
              onClick={hasDetail ? togglePs : undefined}
            >
              {psRunning
                ? <span className="bx-step-dot bx-step-dot--pulse bx-step-dot--amber" />
                : <span className={`bx-step-dot ${psOk ? 'bx-step-dot--done' : 'bx-step-dot--error'}`} />}
              <span className="bx-agent-badge" style={{ color: psRunning ? 'rgba(255,255,255,0.5)' : psOk ? '#86efac' : '#fca5a5', fontSize: 10 }}>
                {psLabel}
              </span>
              {psMeta && <span className="bx-step-message">{psMeta}</span>}
              {hasDetail && <ChevronDown size={9} style={{ marginLeft: 'auto', opacity: 0.35, transform: isPsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }} />}
            </div>

            {hasDetail && isPsExpanded && (
              <div className="bx-terminal-output bx-step-output-scroll" style={{ margin: `2px 0 4px ${baseMarginLeft + 16}px`, fontSize: 10 }}>
                {ps.errors && ps.errors.length > 0 && <div style={{ color: '#fca5a5' }}>{ps.errors.join('\n')}</div>}
                {ps.missing && ps.missing.length > 0 && <div style={{ color: '#fbbf24' }}>{ps.missing.join('\n')}</div>}
                {ps.raw && <div style={{ color: 'rgba(255,255,255,0.7)', whiteSpace: 'pre-wrap' }}>{ps.raw}</div>}
                {ps.parsed && <div style={{ color: 'rgba(255,255,255,0.7)', whiteSpace: 'pre-wrap' }}>{ps.parsed}</div>}
                {ps.nodes && <div style={{ color: 'rgba(255,255,255,0.7)', whiteSpace: 'pre-wrap' }}>{ps.nodes}</div>}
                {ps.avap_code && <div style={{ color: '#86efac', whiteSpace: 'pre-wrap' }}>{ps.avap_code}</div>}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}
